import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Shell from "../../components/Shell";
import api from "../../lib/api";
import { StatusPill } from "./Dashboard";
import { toast } from "sonner";
import { Repeat } from "lucide-react";

export default function Orders() {
    const [orders, setOrders] = useState([]);
    const [expanded, setExpanded] = useState(null);
    const [params] = useSearchParams();
    const confirmed = params.get("confirmed");

    const reload = () => api.get("/orders").then(r => setOrders(r.data || [])).catch(() => {});
    useEffect(() => { reload(); }, []);

    const reorder = async (id) => {
        try {
            const { data } = await api.post(`/orders/${id}/reorder`);
            toast.success(`Reorder created · ${data.order_no}`);
            reload();
        } catch { toast.error("Could not reorder"); }
    };

    return (
        <Shell variant="clinic">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10 animate-fade-in">
                {confirmed && (
                    <div data-testid="order-confirmation" className="mb-8 card-base p-6 border-emerald-200 bg-emerald-50/50">
                        <p className="eyebrow text-emerald-800 mb-1">Order received</p>
                        <h2 className="font-heading text-xl mb-1">Thank you — order <strong>{confirmed}</strong> has been submitted.</h2>
                        <p className="text-sm text-zinc-600">A Dermalis representative will be in touch to confirm processing and delivery.</p>
                    </div>
                )}

                <p className="eyebrow mb-2">Orders</p>
                <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mb-10">Order history</h1>

                {orders.length === 0 ? (
                    <div className="text-center py-20 border border-dashed border-zinc-200 rounded-sm">
                        <p className="text-sm text-zinc-500 mb-3">You haven't placed any orders yet.</p>
                        <Link to="/catalogue" className="btn-primary">Browse catalogue</Link>
                    </div>
                ) : (
                    <div className="card-base divide-y divide-zinc-100" data-testid="orders-list">
                        {orders.map(o => (
                            <div key={o.id}>
                                <div role="button" tabIndex={0} onClick={() => setExpanded(expanded === o.id ? null : o.id)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(expanded === o.id ? null : o.id); } }} className="w-full p-5 flex items-center gap-4 hover:bg-zinc-50 transition-colors text-left cursor-pointer focus:outline-none focus:bg-zinc-50">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-1">
                                            <span className="font-medium text-sm">{o.order_no}</span>
                                            <StatusPill status={o.status} />
                                        </div>
                                        <p className="text-xs text-zinc-500">{new Date(o.created_at).toLocaleString()} · {o.items.length} items · ${o.total.toFixed(2)} {o.currency}</p>
                                    </div>
                                    <button data-testid={`reorder-${o.id}`} onClick={(e) => { e.stopPropagation(); reorder(o.id); }} className="btn-ghost"><Repeat size={14} strokeWidth={1.5} /> Reorder</button>
                                </div>
                                {expanded === o.id && (
                                    <div className="bg-zinc-50/60 p-5 text-sm">
                                        <table className="w-full text-left">
                                            <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                                                <th className="py-2">Product</th><th className="py-2">SKU</th><th className="py-2">Qty</th><th className="py-2 text-right">Subtotal</th>
                                            </tr></thead>
                                            <tbody>
                                                {o.items.map(it => (
                                                    <tr key={it.product_id} className="border-t border-zinc-200">
                                                        <td className="py-2">{it.name}</td>
                                                        <td className="py-2 text-zinc-500">{it.sku}</td>
                                                        <td className="py-2">{it.quantity}</td>
                                                        <td className="py-2 text-right">${it.subtotal.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {o.clinic_notes && <p className="mt-3 text-zinc-600"><span className="eyebrow mr-2">Notes</span>{o.clinic_notes}</p>}
                                        {o.admin_notes && <p className="mt-2 text-zinc-600"><span className="eyebrow mr-2">Dermalis</span>{o.admin_notes}</p>}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Shell>
    );
}
